import React, {useState, useEffect} from 'react'
import {Form} from 'react-bootstrap'
import Select from 'react-select'
import {COUNTRY_CODES} from '../Utils/Constant/WalletCard'
import {validatePhoneNumberRegionWithMessage} from '../Utils/_helpers'
import { useI18n } from '../Utils/_helpers/useI18n'

interface PhoneInputProps {
  value?: string
  countryCode?: string
  onChange?: (phone: string, countryCode: string) => void
  onBlur?: () => void
  placeholder?: string
  label?: string
  required?: boolean
  error?: string
  touched?: boolean
  className?: string
  disabled?: boolean
  name?: string,
  color?: string
}

interface CountryOption {
  value: string
  label: string
  countryCode: string
  flag: string
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  countryCode = 'AU', // Default to Australia
  onChange,
  onBlur,
  placeholder = 'Enter phone number',
  label = 'Phone Number',
  required = false,
  error,
  touched,
  className = '',
  disabled = false,
  name = 'phone',
  color = '#888'
}) => {
  const {t} = useI18n()
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [validationError, setValidationError] = useState('')

  // Convert COUNTRY_CODES to select options
  const countryOptions: CountryOption[] = COUNTRY_CODES.map((country) => ({
    value: country.code,
    label: `${country.flag} ${country.name} ${country.countryCode}`,
    countryCode: country.countryCode,
    flag: country.flag,
  }))

  // Initialize selected country
  useEffect(() => {
    // Find country by countryCode (+61) or by region code (AU)
    const defaultCountry = countryOptions.find((option) => 
      option.countryCode === countryCode || option.value === countryCode
    )
    if (defaultCountry) {
      setSelectedCountry(defaultCountry)
    }
  }, [countryCode])

  // Initialize phone number
  useEffect(() => {
    setPhoneNumber(value)
  }, [value])

  // Custom option component for Select
  const CustomOption = (props: any) => (
    <div
      {...props.innerProps}
      className={`d-flex align-items-center option-country cursor-pointer p-2 ${props.isFocused ? 'bg-light' : ''}`}
    >
      <span className='flag-icon'>{props.data.flag}</span>
      <div className='d-flex flex-column'>
        <span className='country-name'>
          {COUNTRY_CODES.find((c) => c.code === props.data.value)?.name} {props.data.countryCode}
        </span>
      </div>
    </div>
  )

  // Custom single value component for Select
  const CustomSingleValue = (props: any) => (
    <div className='d-flex align-items-center single-value-country'>
      <span className='flag-icon'>{props.data.flag}</span>
      <span>{props.data.countryCode}</span>
    </div>
  )

  const handleCountryChange = (selectedOption: CountryOption | null) => {
    if (selectedOption) {
      setSelectedCountry(selectedOption)
      // Validate phone number with new country code if phone number exists
      if (phoneNumber.trim()) {
        validatePhone(phoneNumber, selectedOption.value)
      } else {
        // Clear validation error if no phone number
        setValidationError('')
      }
      // Return the actual country code (+61) instead of region code (AU)
      onChange?.(phoneNumber, selectedOption.countryCode)
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value
    setPhoneNumber(newPhone)

    if (selectedCountry) {
      // Always validate when phone number changes
      if (newPhone.trim()) {
        validatePhone(newPhone, selectedCountry.value)
      } else {
        // Clear validation error when phone is empty
        setValidationError('')
      }
      onChange?.(newPhone, selectedCountry.countryCode)
    }
  }

  const validatePhone = (phone: string, region: string) => {
    if (!phone.trim()) {
      setValidationError('')
      return
    }

    try {
      const result = validatePhoneNumberRegionWithMessage({
        number: phone,
        region: region,
        validateDuplicate: false,
        contacts: new Map(),
      })

      if (!result.isValid) {
        setValidationError(result.errorMessage)
      } else {
        setValidationError('')
      }
    } catch (err) {
      setValidationError(t('WALLET_CARD.VALIDATION.INVALID_PHONE_NUMBER_FORMAT'))
    }
  }

  const handleBlur = () => {
    if (selectedCountry && phoneNumber.trim()) {
      // Only validate on blur if there's a phone number
      validatePhone(phoneNumber, selectedCountry.value)
    }
    onBlur?.()
  }

  return (
    <div className={`phone-input-container ${className}`}>
      {label && (
        <Form.Label className='d-flex'>
          {label} {required && <span className='text-danger'>*</span>}
        </Form.Label>
      )}

      <div className='phone-input-row'>
        {/* Country Code Selector */}
        <div className='country-dropdown-wrapper'>
          <Select
            value={selectedCountry}
            onChange={handleCountryChange}
            options={countryOptions}
            components={{
              Option: CustomOption,
              SingleValue: CustomSingleValue,
            }}
            isDisabled={disabled}
            isSearchable={false}
            placeholder='Select'
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: '48px',
                height: '48px',
                maxHeight: '48px',
                width: '95px',
                maxWidth: '95px',
                minWidth: '95px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'Poppins, Helvetica, sans-serif',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  borderColor: '#ddd',
                },
                ...(state.isFocused && {
                  borderColor: '#7950F2',
                  boxShadow: '0 0 0 2px rgba(121, 80, 242, 0.2)',
                }),
              }),
              container: (base) => ({
                ...base,
                width: '95px',
                maxWidth: '95px',
                minWidth: '95px',
                height: '48px',
              }),
              singleValue: (base) => ({
                ...base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#555',
                fontWeight: '500',
                margin: '0',
                position: 'static',
                transform: 'none',
              }),
              valueContainer: (base) => ({
                ...base,
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flex: '1',
              }),
              dropdownIndicator: (base) => ({
                ...base,
                color: '#999',
                padding: '0 4px',
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }),
              indicatorsContainer: (base) => ({
                ...base,
                padding: '0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }),
              menu: (base) => ({
                ...base,
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                zIndex: 9999,
                minWidth: '250px',
              }),
            }}
            className='country-select'
          />
        </div>

        {/* Phone Number Input */}
        <div className='phone-number-wrapper'>
          <Form.Control
            type='tel'
            name={name}
            value={phoneNumber}
            onChange={handlePhoneChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            isInvalid={!!(touched && (error || validationError))}
            className='phone-number-input'
          />
        </div>
      </div>

      {/* Error Messages */}
      {touched && (error || validationError) && (
        <div className='fv-plugins-message-container'>
          <span role='alert' className='text-danger'>
            {error || validationError}
          </span>
        </div>
      )}

      {/* Helper Text */}
      {selectedCountry && !error && !validationError && (
        <div className='form-text' style={{color: color}}>
          {t('WALLET_CARD.FORM.SETTING_FORM_CONNECT.FORMAT_PHONE_NUMBER', {
            countryCode: selectedCountry.countryCode,
          })}
        </div>
      )}
    </div>
  )
}

export default PhoneInput
